import { lookupHeaderDepth } from './headerRules';
export type OpeningWall = 'front' | 'back' | 'left' | 'right' | 'divider';
export type OpeningType = 'door' | 'window';
/**
 * Which jamb a door leaf hangs on, seen from OUTSIDE the wall (from the relax
 * room for a divider door) — the way a door is ordered: a "vänsterhängd" door
 * has its hinges on the left and its handle on the right. Absent = left, which
 * is what every door drew before the choice existed (issue 160).
 */
export type DoorHingeSide = 'left' | 'right';
/**
 * Which way the leaf opens, relative to the hot room: `out` swings away from
 * the sauna — to the outside, or into the relax room for the divider door.
 * A sauna door opens outward so it can be pushed open in an emergency
 * (Exacta: "alltid kunna öppnas utåt"; dinbyggare.se). Absent = out (issue 172).
 */
export type DoorSwing = 'out' | 'in';

export interface WallOpening {
  id: string;
  wall: OpeningWall;
  type: OpeningType;
  xMm: number;
  yMm: number;
  widthMm: number;
  heightMm: number;
  glazed?: boolean;
  /** Doors only; see `DoorHingeSide`. */
  hingeSide?: DoorHingeSide;
  /** Doors only; see `DoorSwing`. */
  swing?: DoorSwing;
}

export interface GlazingZone {
  id: string;
  xStartMm: number;
  xEndMm: number;
  yMm: number;
  heightMm: number;
}

export type StudType =
  | 'standard'
  | 'king_stud'
  | 'jack_stud'
  | 'cripple_bottom'
  | 'cripple_top';

export interface StudSegment {
  xMm: number;
  type: StudType;
  yStartMm: number;
  yEndMm: number;
}

export interface HeaderSegment {
  openingId: string;
  xStartMm: number;
  xEndMm: number;
  yMm: number;
  depthMm: number;
  /**
   * True when the span exceeds the header span table's largest entry. The
   * returned depthMm is then the table maximum — NOT a verified dimension —
   * and the header needs an engineered (glulam) beam sized by a structural
   * engineer. Presentation layers must not show the depth as adequate.
   */
  exceedsSpanTable?: boolean;
}

export interface SillSegment {
  openingId: string;
  xStartMm: number;
  xEndMm: number;
  yMm: number;
}

export interface GenerateFramedWallInput {
  wallLengthMm: number;
  wallHeightMm: number;
  openings?: WallOpening[];
  glazingZones?: GlazingZone[];
  ccMm?: number;
  studWidthMm?: number;
}

export interface FramedWallResult {
  studs: StudSegment[];
  headers: HeaderSegment[];
  sills: SillSegment[];
  requiresLateralSupport: boolean;
}

function dedupeStuds(studs: StudSegment[]): StudSegment[] {
  const map = new Map<string, StudSegment>();
  for (const stud of studs) {
    const key = `${stud.type}:${stud.xMm}:${stud.yStartMm}:${stud.yEndMm}`;
    map.set(key, stud);
  }
  return [...map.values()].sort((a, b) => a.xMm - b.xMm || a.yStartMm - b.yStartMm);
}

import { getRuleValue, type RuleOverrides } from '../../data/ruleRegistry';
import { validateWallOpenings } from '../openings/validateOpeningSet';

/**
 * Look up header depth from span table, rounding up to next entry.
 * Spans beyond the table are flagged instead of silently extrapolated —
 * falling back to the last entry presented a 3.2 m panorama header as an
 * ordinary 220 mm C24 when it needs an engineered beam.
 */

export function generateFramedWall(input: GenerateFramedWallInput, overrides: RuleOverrides = {}): FramedWallResult {
  const {
    wallLengthMm,
    wallHeightMm,
    openings = [],
    glazingZones = [],
    ccMm = getRuleValue<number>('framing.ccMm', overrides),
    studWidthMm = getRuleValue<number>('framing.studWidthMm', overrides),
  } = input;

  const issues = validateWallOpenings({
    openings,
    wallLengthMm,
    wallHeightMm,
    minEdgeClearanceMm: studWidthMm,
    checkFraming: false,
  });
  if (issues.length > 0) {
    const issue = issues[0];
    throw new Error(
      issue.openingId
        ? `Öppning ${issue.openingId}: ${issue.message}`
        : issue.message,
    );
  }
  for (const zone of glazingZones) {
    if (zone.xStartMm < 0 || zone.xEndMm > wallLengthMm || zone.xEndMm <= zone.xStartMm) {
      throw new Error(`Glasyta ${zone.id}: ligger utanför väggens mått`);
    }
    if (zone.yMm < 0 || zone.yMm + zone.heightMm > wallHeightMm) {
      throw new Error(`Glasyta ${zone.id}: höjd ligger utanför väggens mått`);
    }
  }

  // Studs sit on the syll (bottom plate) and end below the hammarband (top plate)
  const plateThickness = studWidthMm;
  const studBottom = plateThickness;
  const studTop = wallHeightMm - plateThickness;

  // A framed panel needs a stud at BOTH ends. Stepping by c/c only lands on
  // wallLengthMm when the length divides evenly — every other wall silently
  // lost its terminal stud (2500 mm wall: studs at 0..2400, none at 2500).
  const studs: StudSegment[] = [];
  for (let x = 0; x < wallLengthMm; x += ccMm) {
    studs.push({ xMm: x, type: 'standard', yStartMm: studBottom, yEndMm: studTop });
  }
  studs.push({ xMm: wallLengthMm, type: 'standard', yStartMm: studBottom, yEndMm: studTop });

  const headers: HeaderSegment[] = [];
  const sills: SillSegment[] = [];

  for (const zone of glazingZones) {
    const zoneHeader = lookupHeaderDepth(zone.xEndMm - zone.xStartMm, overrides);
    // Top cripples rest on the header's TOP edge — starting them at the
    // zone top buries them inside the full header depth. The header itself
    // may be deeper than the space under the top plate (panorama spans),
    // so the cripple start is clamped to studTop, which simply skips them.
    const zoneHeaderTopMm = Math.min(zone.yMm + zone.heightMm + zoneHeader.depthMm, studTop);
    for (let index = studs.length - 1; index >= 0; index -= 1) {
      const stud = studs[index];
      if (
        stud.type === 'standard' &&
        stud.xMm > zone.xStartMm &&
        stud.xMm < zone.xEndMm
      ) {
        studs.splice(index, 1);
        if (zone.yMm > studBottom) {
          studs.push({
            xMm: stud.xMm,
            type: 'cripple_bottom',
            yStartMm: studBottom,
            yEndMm: zone.yMm,
          });
        }
        if (zoneHeaderTopMm + studWidthMm <= studTop) {
          studs.push({
            xMm: stud.xMm,
            type: 'cripple_top',
            yStartMm: zoneHeaderTopMm,
            yEndMm: studTop,
          });
        }
      }
    }

    headers.push({
      openingId: zone.id,
      xStartMm: zone.xStartMm,
      xEndMm: zone.xEndMm,
      yMm: zone.yMm + zone.heightMm,
      depthMm: zoneHeader.depthMm,
      ...(zoneHeader.exceedsSpanTable ? { exceedsSpanTable: true } : {}),
    });
  }

  for (const op of openings) {
    const openingHeader = lookupHeaderDepth(op.widthMm, overrides);
    // Top cripples rest on the header's TOP edge (clamped to studTop when a
    // deep header fills the whole space under the top plate).
    const headerTopMm = Math.min(op.yMm + op.heightMm + openingHeader.depthMm, studTop);
    const hasSill = op.type === 'window' && op.yMm > 0;
    // Bottom cripples carry the sill (one stud-width tall, top edge at the
    // opening bottom) — without a sill they run to the opening itself.
    const crippleBottomTopMm = hasSill ? op.yMm - studWidthMm : op.yMm;

    // Studs are centred on their xMm, so anything closer than a stud width
    // to a king/jack line physically overlaps it. Kings sit one stud width
    // outside the opening — grid studs within two stud widths of the edge
    // must go, or they render inside the jack/king (full-volume z-fight
    // when an opening edge lands exactly on the cc grid).
    const removeMinMm = op.xMm - 2 * studWidthMm;
    const removeMaxMm = op.xMm + op.widthMm + 2 * studWidthMm;
    const collidingXPositions = new Set(studs.filter(
      (stud) =>
        (stud.type === 'standard' || stud.type === 'cripple_bottom' || stud.type === 'cripple_top') &&
        stud.xMm > removeMinMm &&
        stud.xMm < removeMaxMm,
    ).map((stud) => stud.xMm));

    for (const xMm of collidingXPositions) {
      for (let index = studs.length - 1; index >= 0; index -= 1) {
        const stud = studs[index];
        if (
          stud.xMm === xMm &&
          (stud.type === 'standard' || stud.type === 'cripple_bottom' || stud.type === 'cripple_top')
        ) {
          studs.splice(index, 1);
        }
      }
      // Cripples only where they stay clear of the jack studs — a cripple
      // within a stud width of the opening edge would overlap the jack.
      const clearOfJacks =
        xMm >= op.xMm + studWidthMm && xMm <= op.xMm + op.widthMm - studWidthMm;
      if (!clearOfJacks) continue;
      // Skip cripples shorter than a stud width — a 15mm sliver between a
      // door header and the top plate is not a real member.
      if (op.yMm > studBottom && crippleBottomTopMm >= studBottom + studWidthMm) {
        studs.push({
          xMm,
          type: 'cripple_bottom',
          yStartMm: studBottom,
          yEndMm: crippleBottomTopMm,
        });
      }
      // NOTE: the headerTopMm clamp above also covers main's negative-length
      // guard for openings reaching past the top-plate line — headerTopMm is
      // clamped to studTop, so the cripple is simply skipped there.
      if (headerTopMm + studWidthMm <= studTop) {
        studs.push({
          xMm,
          type: 'cripple_top',
          yStartMm: headerTopMm,
          yEndMm: studTop,
        });
      }
    }

    const kingLeft = Math.max(0, op.xMm - studWidthMm);
    const kingRight = Math.min(wallLengthMm, op.xMm + op.widthMm + studWidthMm);

    studs.push({ xMm: kingLeft, type: 'king_stud', yStartMm: studBottom, yEndMm: studTop });
    studs.push({ xMm: kingRight, type: 'king_stud', yStartMm: studBottom, yEndMm: studTop });

    studs.push({
      xMm: op.xMm,
      type: 'jack_stud',
      yStartMm: studBottom,
      yEndMm: op.yMm + op.heightMm,
    });
    studs.push({
      xMm: op.xMm + op.widthMm,
      type: 'jack_stud',
      yStartMm: studBottom,
      yEndMm: op.yMm + op.heightMm,
    });

    headers.push({
      openingId: op.id,
      xStartMm: op.xMm,
      xEndMm: op.xMm + op.widthMm,
      yMm: op.yMm + op.heightMm,
      depthMm: openingHeader.depthMm,
      ...(openingHeader.exceedsSpanTable ? { exceedsSpanTable: true } : {}),
    });

    // Sill for windows (not doors which sit at floor level)
    if (hasSill) {
      sills.push({
        openingId: op.id,
        xStartMm: op.xMm,
        xEndMm: op.xMm + op.widthMm,
        yMm: op.yMm,
      });
    }
  }

  return {
    studs: dedupeStuds(studs),
    headers,
    sills,
    requiresLateralSupport: glazingZones.length > 0,
  };
}
